import * as React from 'react';

import {
  DataGrid,
  ListScreenLayout,
  Pagination,
  SearchField,
  Button,
  useDataGridSelection,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucidePlusCircle } from 'lucide-react';

import {
  $openCreateOrganizationDialog,
  $openEditOrganizationDialog,
  $openResetOrganizationDialog,
  $setOrganizationOnboardedStatus,
} from '../../_state/organizationDialog.state';
import * as state from '../../_state/organizationList.state';
import { useTranslations } from '../../i18n/useTranslations';
import { adminRoutes } from '../../routes';
import { OrganizationRecord } from '../../types';

import { useOrganizationListColumns } from './hooks/useOrganizationListColumns';
import { OrganizationDetailsDialog } from './OrganizationDetailsDialog';
import { ResetOrganizationDialog } from './ResetOrganizationDialog';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

//
// ⚛️ ⚛️ ⚛️ ORGANIZATIONS LIST VIEW ⚛️ ⚛️ ⚛️
//
export const OrganizationsListView = () => {
  const t = useTranslations();
  const [, navigate] = useModuleLocation();

  // States
  const [_selectedRows, setSelectedRows] = React.useState<OrganizationRecord[]>(
    [],
  );

  // Atoms
  const {
    data: results,
    isFetching,
    loadingState,
    error,
  } = useAtomValue(state.$organizations);
  const [limit = 25, setLimit] = useAtom(state.$limit);
  const [offset, setOffset] = useAtom(state.$offset);
  const [searchParam, setSearchParam] = useAtom(state.$searchParam);
  const openCreateDialog = useSetAtom($openCreateOrganizationDialog);
  const openEditDialog = useSetAtom($openEditOrganizationDialog);
  const setOnboardedStatus = useSetAtom($setOrganizationOnboardedStatus);
  const openResetDialog = useSetAtom($openResetOrganizationDialog);

  // Other data hooks
  const handleSelectionChange = useDataGridSelection(
    results?.organizations ?? [],
    setSelectedRows,
  );
  const columns = useOrganizationListColumns({
    onEdit: openEditDialog,
    onViewDetails: (row) =>
      navigate(adminRoutes.organizationDetails, {
        params: { organizationId: row.id },
      }),
    onSetOnboarded: ({ organizationId, onboarded }) =>
      setOnboardedStatus({ organizationId, onboarded }),
    onReset: (row) => openResetDialog({ id: row.id, name: row.name }),
  });

  // Callbacks
  const handlePaginationChange = React.useCallback(
    ({
      limit: newLimit,
      offset: newOffset,
    }: {
      limit: number;
      offset: number;
    }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );

  const organizations = results?.organizations ?? [];
  const total = results?.total ?? 0;
  const emptyPanelProps = {
    title: t.organizationListView.empty.title,
    description: t.organizationListView.empty.description,
  };

  return (
    <>
      <ListScreenLayout
        className="@OrganizationsListView"
        filters={
          <SearchField
            autoFocus
            aria-label={t.organizationListView.searchPlaceholder}
            placeholder={t.organizationListView.searchPlaceholder}
            className="w-64"
            value={searchParam ?? ''}
            onChange={(value) => {
              setSearchParam(value ?? undefined);
              setOffset(0);
            }}
          />
        }
        actions={
          <Button
            onPress={openCreateDialog}
            variant="solid"
            color="primary"
            icon={<LucidePlusCircle />}
          >
            {t.organizationListView.actions.provision}
          </Button>
        }
        footer={
          total > limit ? (
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChange}
            />
          ) : undefined
        }
      >
        <DataGrid
          columns={columns}
          dataSource={organizations}
          fullWidth
          isLoading={loadingState.isInitialLoading || isFetching}
          onSelectionChange={handleSelectionChange}
          rowKey="id"
          selectionMode="multiple"
          hasError={!!error}
          errorPanelProps={
            error ? { description: error.message, centered: true } : undefined
          }
          emptyPanelProps={emptyPanelProps}
        />
      </ListScreenLayout>
      <OrganizationDetailsDialog />
      <ResetOrganizationDialog />
    </>
  );
};
